import {Session} from 'com/gongxigames/math/core/game/session/Session';
import {PPRegulationTypes} from '@gongxigames/framework/integrations/pariplay/PPRegulationTypes';
import {PPRequest} from '@gongxigames/framework/integrations/pariplay/request/PPRequest';
import {PPLoadGameResponse} from '@gongxigames/framework/integrations/pariplay/response/PPLoadGameResponse';
import {PPSettings} from '@gongxigames/framework/integrations/pariplay/PPSettings';
import {PPGameClientWrapper} from '@gongxigames/framework/integrations/pariplay/PPGameClientWrapper';
import {PPConfiguration} from '@gongxigames/framework/integrations/pariplay/PPConfiguration';
import {IBrowser} from '@gongxigames/framework/core';
import {ServerMessage, SpecialWinTypeConfig, Xhr} from '@gongxigames/framework/slot';

const LocalesWithoutJackpotByRegulation: Partial<Record<PPRegulationTypes, string[]>> = {
    [PPRegulationTypes.SZ_Switzerland]: ['it', 'en', 'fr', 'de'],
    [PPRegulationTypes.German]: ['de', 'en']
};

const isWithoutJackpot = (regulation: PPRegulationTypes, lang: string ): boolean => {
    const locales = LocalesWithoutJackpotByRegulation[regulation] || [];

    return locales.includes(lang);
};

export class PPLoadGameRequest extends PPRequest<PPLoadGameResponse> {
    protected settings: PPSettings;
    protected client: PPGameClientWrapper;

    constructor(config: PPConfiguration, browser: IBrowser, settings: PPSettings, client: PPGameClientWrapper) {
        super(config, browser);
        this.settings = settings;
        this.client = client;
    }

    protected getURL(): string {
        return this.config.RgsUrl + '/client/loadgame';
    }

    protected getParams(): string {
        const data: any = {};

        if (this.config.GameCode) {
            data.GameCode = this.config.GameCode;
        }

        if (this.config.CurrencyCode) {
            data.CurrencyCode = this.config.CurrencyCode;
        }

        if (this.config.LanguageCode) {
            data.LanguageCode = this.config.LanguageCode;
        }

        if (this.config.ExtraData) {
            data.ExtraData = this.config.ExtraData;
        }

        if (this.config.extraData) {
            data.extraData = this.config.extraData;
        }

        return Xhr.objectToKeyValue(data);
    }

    protected processResponse(data: any): void {
        this.response = new PPLoadGameResponse(data);

        if (this.response.source) {
            this.settings.set('autoplayValues', this.response.getAutoPlayValues());
            this.settings.set('ukRegulations', this.response.isUKRegulations());
            this.settings.set('currency', this.response.getCurrencyCode());
            this.settings.set('showBalance', this.response.shouldShowBalance());
            this.settings.set('showBet', this.response.shouldShowStake());
            this.settings.set('soundEnabled', this.isSoundEnabled());
            this.settings.set('fullscreenDisabled', this.config.disableFullScreen);

            this.config.BalanceURL = this.response.getBalanceURL();
            this.config.EndFreeGameUrl = this.response.getEndFreeGameUrl();
            this.config.EndUrl = this.response.getEndUrl();
            this.config.NotifyUrl = this.response.getNotifyUrl();
            this.config.PlaceBetUrl = this.response.getPlaceBetUrl();
            this.config.playerTokenID = this.response.getPlayerTokenID();
            this.config.DefaultStake = this.response.getBet();
            this.config.DefaultBet = this.response.getBet();

            if (this.config.sound !== undefined) {
                this.settings.set('soundVolume', this.config.sound);
            }

            this.settings.set('homeUrl', this.response.getHomeUrl() || this.config.HomeUrl);
            this.settings.set('cashierUrl', this.response.getCashierUrl() || this.config.CashierUrl);

            if (this.response.getInGameHistoryUrl()) {
                this.settings.set('historyUrl', this.response.getInGameHistoryUrl());
            }

            if (this.response.getDontLoadUrlFromParent()) {
                this.settings.set('dontLoadUrlFromParent', this.response.getDontLoadUrlFromParent());
            }

            const regulationSettings: {
                DelayBetweenSpins: number;
                HasAutobet: boolean;
                HasJackpot: boolean;
                HideCoins: boolean;
                MaxStake: boolean;
                RegulationId: boolean;
                StopSpeedUpSettings: boolean;
                WinProbability: boolean;
                NeedPlayerInput: boolean;
                ReplaceHelpText: boolean;
                SnapTinyWin: boolean;
            } = this.response.source.RegulationSettings;
            const lang = this.settings.get('lang');
            let regulationType = null;

            if (this.config.RegulationTypeFromURL) {
                regulationType = this.config.RegulationTypeFromURL;
            }

            if (regulationSettings && !regulationType) {
                regulationType = regulationSettings.RegulationId;
            }

            if (regulationType) {
                this.settings.set('hideJackpotWords', isWithoutJackpot(regulationType as PPRegulationTypes, lang));

                if (regulationSettings) {
                    if (regulationSettings.StopSpeedUpSettings) {
                        this.settings.set('turboModeDisabled', true);
                        this.settings.set('quickStopDisabled', true);
                    }

                    if (regulationSettings.DelayBetweenSpins) {
                        this.settings.set('roundDelay', regulationSettings.DelayBetweenSpins * 1000);
                    }

                    this.settings.set('autoSpinDisabled', !regulationSettings.HasAutobet);
                }

                switch (regulationType) {
                    case PPRegulationTypes.UK: {
                        this.settings.set('autoplayDefaultSelectedSpins', -1);
                        this.settings.set('autoplayRequiredLossLimit', true);
                        this.settings.set('specialWinTypeConfig', SpecialWinTypeConfig);
                        break;
                    }
                    case PPRegulationTypes.SZ_Switzerland: {
                        this.settings.set('minMaxBetVisible', true);
                        break;
                    }
                    case PPRegulationTypes.German: {
                        this.settings.set('showWinProbability', true);
                        break;
                    }
                    case PPRegulationTypes.Netherlands: {
                        this.settings.set('nonMinimalBetPopupEnabled', true);
                        break;
                    }
                    default: {
                        break;
                    }
                }
            }
        }

        this.callback();
    }

    protected isSoundEnabled(): boolean {
        const value = this.config.sound;

        return value === undefined || (!isNaN(value) && isFinite(value) && value >= 0);
    }

    public getRPBody(): any {
        return this.response.getRPBody();
    }

    public getBalance(): number {
        return this.response.getBalance();
    }

    public process(wrapper: PPGameClientWrapper, response: ServerMessage): ServerMessage[] {
        const msgs: ServerMessage[] = [];

        msgs.push(response);
        const balance = this.response.getBalance() >= 0 ? this.response.getBalance() : -1;

        const session: Session = wrapper.client.getSession();

        session.getPlayer().setBalance(balance);

        // currency
        // default bet
        return msgs;
    }
}
